import { QueryResultDto } from '../../../../../../../typescript-axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef } from 'react';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { TableParameters } from '../config';
import { useMemo } from 'react';

interface TableVisualizationProps {
    data: QueryResultDto;
    parameters?: TableParameters;
};

type Row = Record<string, unknown>;

const INDEX_WIDTH = 60;

const VirtuosoTableComponents: TableComponents<Row> = {
    Scroller: forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{ maxHeight: '100%' }} />
    )),
    Table: (props) => (
        // fixed layout + explicit width => table can be wider than the container => horizontal scroll
        <Table {...props} stickyHeader size="small" sx={{ tableLayout: 'fixed', width: 'max-content' }} />
    ),
    TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
    TableRow,
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

export function TableVisualization({ data, parameters }: TableVisualizationProps) {
    const numRows = typeof parameters?.num_rows === 'number' ? parameters.num_rows : undefined;
    const rows = (numRows ? data.rows.slice(0, numRows) : data.rows) as Row[];
    const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

    // measure a stable width per column from ALL rows, so it doesn't shift while scrolling
    const columnWidths = useMemo(() => {
        const CHAR_PX = 8;    // approx px per character at this font size
        const PADDING = 32;   // MUI cell horizontal padding
        const MIN = 60;
        const MAX = 400;
        const widths: Record<string, number> = {};
        for (const col of columns) {
            let maxLen = col.length;
            for (const row of rows) {
                const len = String(row[col] ?? '').length;
                if (len > maxLen) maxLen = len;
            }
            widths[col] = Math.min(MAX, Math.max(MIN, maxLen * CHAR_PX + PADDING));
        }
        return widths;
    }, [rows, columns]);

    return (
        <TableVirtuoso
            style={{ height: '100%' }}
            data={rows}
            components={VirtuosoTableComponents}
            fixedHeaderContent={() => (
                <TableRow>
                    <TableCell sx={{ backgroundColor: 'background.paper', width: INDEX_WIDTH }}>
                        <strong>#</strong>
                    </TableCell>
                    {columns.map(col => (
                        <TableCell
                            key={col}
                            sx={{ backgroundColor: 'background.paper', width: columnWidths[col] }}
                        >
                            <strong>{col}</strong>
                        </TableCell>
                    ))}
                </TableRow>
            )}
            itemContent={(index, row) => (
                <>
                    <TableCell sx={{ color: 'text.secondary', width: INDEX_WIDTH }}>
                        {index + 1}
                    </TableCell>
                    {columns.map(col => {
                        const value = String(row[col] ?? '');
                        return (
                            <TableCell
                                key={col}
                                title={value}
                                sx={{
                                    width: columnWidths[col],
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {value}
                            </TableCell>
                        );
                    })}
                </>
            )}
        />
    );
}